<script setup lang="ts">
import type { FormDrawerType } from '~/composables/useFormDrawer'

const { t } = useI18n()
const route = useRoute()

const forms = computed<{ value: FormDrawerType, label: string }[]>(() => [
  { value: 'book', label: t('book') },
  { value: 'prescription', label: t('prescription') },
])

const selected = ref<FormDrawerType>('book')

const requested = useFormDrawer()
const { isActive, open, close } = useMagicDrawer('form-drawer')

onMounted(() => {
  watch(requested, (type) => {
    if (!type)
      return
    selected.value = type
    open()
  }, { immediate: true })
})

watch(isActive, (active) => {
  if (!active)
    requested.value = null
})

watch(() => route.path, close)
</script>

<template>
  <aside class="formDrawer contents">
    <MagicDrawerProvider
      id="form-drawer"
      :options="{
        position: 'right',
        scrollLock: {
          padding: false,
        },
      }"
    >
      <MagicDrawerBackdrop />
      <MagicDrawerContent>
        <div class="c-$primary-color bg-white flex flex-col size-full of-auto">
          <div class="container flex flex-none h-$header-height items-center justify-between">
            <Text as="h2" class="typo-sans-1 uppercase" :text="$t('get_in_touch')" />
            <ButtonNav :title="$t('close')" @click="close" />
          </div>
          <div class="container pb-10 space-y-4 lg:space-y-9">
            <ul class="formDrawer-switch flex flex-wrap gap-1.5">
              <li v-for="form in forms" :key="form.value">
                <Button
                  :title="form.label"
                  :class="{ 'is-active': selected === form.value }"
                  :aria-pressed="selected === form.value"
                  @click="selected = form.value"
                />
              </li>
            </ul>
            <TransitionDefault mode="out-in">
              <BookForm v-if="selected === 'book'" submit-align="start" />
              <PrescriptionForm v-else submit-align="start" />
            </TransitionDefault>
          </div>
        </div>
      </MagicDrawerContent>
    </MagicDrawerProvider>
  </aside>
</template>

<style>
.formDrawer {
  --magic-drawer-height: 100%;
  --magic-drawer-drag-overshoot: 0rem;

  --magic-drawer-enter-animation: slide-rtl-in 0.7s var(--ease-custom-expo);
  --magic-drawer-leave-animation: slide-rtl-out 0.6s var(--ease-custom-expo);

  @screen lg {
    --magic-drawer-drag-overshoot: 50vw;
  }
  @screen 4xl {
    --magic-drawer-drag-overshoot: 70vw;
  }
}

.formDrawer-switch .button:not(.is-active) > span {
  opacity: 30%;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      opacity: 100%;
    }
  }
}
</style>
